import type { BeforeSendEvent } from "@vercel/analytics/react";

/**
 * Vercel Web Analytics `beforeSend` middleware.
 *
 * The generic React integration has no route templates (those exist only for
 * the Next/Remix/SvelteKit integrations), so a Vite SPA would report concrete
 * URLs: festival ids, invite tokens in the query string, public timetable
 * slugs. This keeps the dashboard readable (one row per screen) while sending
 * nothing that identifies a person, a festival or a one-time link:
 *
 * - token-bearing routes are dropped entirely (`null`);
 * - the query string and the hash are removed;
 * - UUID path segments become `:id`;
 * - `/live/<slug>` becomes `/live/:slug`.
 *
 * No custom `track()` calls anywhere: the product funnel lives in our own
 * database (`/admin/funnel`), this script only counts screens.
 */

const TOKEN_ROUTES = [
  "/verify-email",
  "/reset-password",
  "/accept-invite",
  "/accept-artist-invite",
  "/accept-crew-invite",
  "/unsubscribe",
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isTokenRoute(pathname: string): boolean {
  return TOKEN_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

/** Collapse identifying segments; no trailing slash except on the root. */
export function scrubPathname(pathname: string): string {
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => (UUID_RE.test(segment) ? ":id" : segment));
  if (segments[0] === "live" && segments.length > 1) segments[1] = ":slug";
  return segments.length > 0 ? `/${segments.join("/")}` : "/";
}

export function scrubAnalyticsUrl(
  event: BeforeSendEvent,
): BeforeSendEvent | null {
  const raw = event.url;

  if (raw.startsWith("/")) {
    // Relative form: stay relative, drop query and hash.
    const pathname = raw.split(/[?#]/, 1)[0] ?? raw;
    if (isTokenRoute(pathname)) return null;
    return { ...event, url: scrubPathname(pathname) };
  }

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (isTokenRoute(url.pathname)) return null;
  url.search = "";
  url.hash = "";
  url.pathname = scrubPathname(url.pathname);
  return { ...event, url: url.toString() };
}
